import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { ChatbotContext, ChatbotContextPayload } from '../../../../../context/chatbotContext';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { IntegrationContext, IntegrationContextPayload } from '../../../../../context/integrationContext';
import ShowAvailableSlackChannels from './showAvailableChannels';
import { Grid, useMediaQuery } from '@mui/material';
import GlobalButton from '../../../../utility/globalButton/button';
import IconsColor from '../../../../utility/iconsColor';
import { useSearchParams } from 'react-router-dom';
import agent from '../../../../../services/api';
import FacebookInformationCard from '../../../../utility/facebookInformationCard';
import GlobalDeletePopup from '../../../../utility/globalDeletePopup/globalDeletePopup';
import NoAuthorisedSlackAccount from './noAuthorisedSlackAccount';
import globalStyles from "../../../../../assets/styles/global.module.scss";
import { chatbotIntegrationAssignmentData, createChatbotSlackIntegrationPayload, globalSlackIntegration, slackChannelData } from '../../../../../models/axiosModel';
import { GenericDataContext, GenericDataContextPayload } from '../../../../../context/genericDataContext';
import { AIChatbotBreadcrumb, chatbotDetailsBreadcrumb, chatbotIntegrationsBreadcrumb, homeBreadcrumb } from '../../../../../utils/breadcrumb';
import { useGenericStore } from '../../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';

export interface extendedSlackChannelData {
    slack_channel_id: number,
    channel_id: string,
    slack_channel_name: string,
    slack_channel_purpose: string,
    slack_channel_meta_data: string,
    timestamp: string,
    chatbot_slack_assignment_data: chatbotIntegrationAssignmentData[], 
    slack_integration_id: number, // 
    slack_team_name: string, //
}

export interface extendedSlackIntegration { 
    slack_integration_id: number,
    slack_user_id: string,
    slack_user_name: string,
    slack_user_real_name: string,
    slack_team_id: string,
    slack_team_name: string,
    slack_access_token_type: string,
    slack_user_access_token: string,
    slack_bot_access_token: string,
    slack_integration_meta_data: string,
    slack_channels: extendedSlackChannelData[]
} 

interface updatedSlackChannelData extends slackChannelData {
    username: string,
}

export interface confirmationPopoverInterface {
    flagOpen: boolean, 
    anchorEl: HTMLButtonElement | null, 
    channel_name: string,
} 

interface delinkedChannelDetailsInterface {
    flagDelink: boolean, 
    channelDetails: updatedSlackChannelData | null,
}

const ChatbotSlackIntegration = () => {
    const smallScreen = useMediaQuery('(max-width : 900px) and (min-width : 0px)')
    const { chatBotDetails, GetChatbotDataApi } = useContext(ChatbotContext) as ChatbotContextPayload;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { globalIntegrationTypeList } = useContext(IntegrationContext) as IntegrationContextPayload
    const { slackIntegrations, getSlackIntegrations } = useContext(IntegrationContext) as IntegrationContextPayload; 
    const { setBreadCrumbs } = useContext(GenericDataContext) as GenericDataContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))
    
    const [searchParams] = useSearchParams()
    const chatbot_id = searchParams.get('chatbot_id');
    let pathname = window.location.pathname.toLowerCase();

    const [flagShowAvailableChannels, setFlagShowAvailableChannels] = useState(false)
    const [availablePagesData, setAvailablePagesData] = useState<extendedSlackIntegration[]>([])
    const [delinkedChannelDetails, setDelinkedChannelDetails] = useState<delinkedChannelDetailsInterface>({
        flagDelink: false,
        channelDetails: null, 
    })
    const [addChatbotIntegrationDetails, setAddChatbotIntegrationDetails] = useState<createChatbotSlackIntegrationPayload>({
        chatbot_id: 0,
        slack_channel_id: [],
        slack_integration_id: 0,
    })
    const [confirmationPopover, setConfirmationPopover] = useState<confirmationPopoverInterface>({
        flagOpen: false,
        anchorEl: null, 
        channel_name: "",
    });
    
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>, channel_name: string) => {
        setConfirmationPopover(({ ...confirmationPopover, anchorEl: event.currentTarget, flagOpen: true, channel_name: channel_name }));
    };

    const handleCloseConfirmationPopover = () => {
        setConfirmationPopover(({ ...confirmationPopover, anchorEl: null, flagOpen: false, channel_name: "" }));
    };

    useEffect(() => {
        if (!slackIntegrations) {
            getSlackIntegrations()
        }

        const updatedBreadcrumbs = [
            {...homeBreadcrumb}, 
            AIChatbotBreadcrumb, 
            {...chatbotDetailsBreadcrumb, name: chatBotDetails?.chatbot_name ? chatBotDetails?.chatbot_name : "Chatbot", path: `/chatbot/viewchatbot/basicdetail?chatbot_id=${chatbot_id}`}, 
            ...((pathname === "/chatbot/viewchatbot/channels/slack") ? [{ name: "Slack Integration", path: `${pathname}?chatbot_id=${chatbot_id}` , isActive: true}] : []),
            ...((pathname === "/chatbot/viewchatbot/integration/slack") ? [{...chatbotIntegrationsBreadcrumb, path: `/chatbot/viewchatbot/integration?chatbot_id=${chatbot_id}`}, {name: "Slack", path: `${pathname}?chatbot_id=${chatbot_id}`, isActive: true}] : []),
        ]; 
        setBreadCrumbs(updatedBreadcrumbs);
    }, [chatBotDetails])

    useEffect(() => {
        if (slackIntegrations) {
            let temp: extendedSlackIntegration[] = slackIntegrations.map((slack: globalSlackIntegration) => ({ ...slack, slack_channels: slack.slack_channels.map((channel: slackChannelData) => ({ ...channel, slack_integration_id: slack.slack_integration_id, slack_team_name: slack.slack_team_name })) }))
            setAvailablePagesData(temp)
        }
    }, [slackIntegrations, chatBotDetails])

    async function handleRemoveChatbotIntegration(chatbot_slack_assignment_id: string) {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const response = await agent.deleteChatbotSlackIntegration.delete(chatbot_slack_assignment_id)
        if (response.status) {
            let chatbot_id = Number(searchParams.get('chatbot_id'));
            getSlackIntegrations()
            GetChatbotDataApi(chatbot_id)
        } else {
            handleError(response);
        }
        setDelinkedChannelDetails(({ ...delinkedChannelDetails, flagDelink: false, channelDetails: null }))
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))

    }

    async function saveSlackIntegrationDetails() {
        handleCloseConfirmationPopover()
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const response = await agent.createChatbotSlackIntegration.post(addChatbotIntegrationDetails)
        if (response.status) {
            let chatbot_id = Number(searchParams.get('chatbot_id'))
            showSuccess("Integration Successfull!")
            // setGetChatbotIntegrationData(response.data)
            getSlackIntegrations()
            if (chatbot_id) {
                GetChatbotDataApi(chatbot_id)
            }
        } else {
            handleError(response);
        }
        setFlagShowAvailableChannels(false)
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    function AddChatbotSlackIntegration(slack_integration_id: number, channel_id: number, display_page_name: string, event?: React.MouseEvent<HTMLButtonElement>) {
        if (event) {
            handleClick(event, display_page_name);
        }
        let chatbot_id = searchParams.get('chatbot_id')
        if (chatbot_id) {
            setAddChatbotIntegrationDetails(({
                ...addChatbotIntegrationDetails,
                slack_integration_id: slack_integration_id,
                slack_channel_id: [channel_id],
                chatbot_id: Number(chatbot_id),
            }))
        }
    }

    const pollForCallbackStatus = (stateValue: string) => {
        const pollInterval = 8000;
        const maxPollingTime = 80000;
        let elapsedTime = 0;

        const intervalId = setInterval(async () => {
            try {
                const response = await agent.getCallbackStatus.get(stateValue);

                if (response.status) {
                    const integrationStatus = response?.data?.integration_status?.toLowerCase();

                    if (integrationStatus === 'completed' || integrationStatus === 'failed') {
                        clearInterval(intervalId);

                        if (integrationStatus === 'failed') {
                            alert(response?.data?.reason);
                        } else {
                            // makeIntegrationDataCall(response?.data);
                            getSlackIntegrations()
                        }
                    } else {
                        console.log("Callback not completed, retrying...");
                    }

                    elapsedTime += pollInterval;

                    if (elapsedTime >= maxPollingTime) {
                        console.log("Polling stopped after 1 minute.");
                        clearInterval(intervalId);
                    }
                } else {
                    handleError(response);
                }
            } catch (error) {
                console.error("Error polling for callback status:", error);
                clearInterval(intervalId);
            } finally {
                setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
            }
        }, pollInterval);
    };

    async function handleIntegrateSlack() {
        try {
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
            let slackIntegration = globalIntegrationTypeList.find((integration) => integration.integration_app_name == "Slack")
            let currentUrl = window.location.href;
            if (slackIntegration && slackIntegration.integration_app_id) {
                const payload = {
                    current_url: currentUrl,
                    integration_app_id: slackIntegration.integration_app_id,
                };

                const response = await agent.calendlyAuthUrlApi.post(payload);
                if (response.status) {
                    const authUrl = response?.data?.auth_url;
                    if (authUrl) {
                        window.open(response.data.auth_url, "_blank")
                        const stateValue = new URL(authUrl).searchParams.get('state');
                        if (stateValue) {
                            pollForCallbackStatus(stateValue);
                        } else {
                            console.error('State param not found in the auth_url.');
                        }
                    }
                } else {
                    handleError(response);
                }
            }
        } catch (error) {
            console.error(error)
        }
    } 

    return (
        <>
            <>
                {(slackIntegrations && slackIntegrations?.length > 0) ?
                    <>
                        {
                            chatBotDetails && chatBotDetails.chatbot_slack_data.length > 0 ?
                                <>
                                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.gapLength} `} sx={{ padding: { xs: "15px 7px", lg: "15px 15px 15px 5px" } }} mt={{ xs: 1, lg: 0 }}>
                                        <Grid item>
                                            <Grid container className={globalStyles.gapLess}>
                                                {chatBotDetails.chatbot_slack_data.flatMap((slack: globalSlackIntegration) => slack.slack_channels.map((channel: slackChannelData) => ({ ...channel, username: slack.slack_user_real_name }))).map((channel: any, index: number) => {
                                                    return <FacebookInformationCard
                                                            integrationType='Slack'
                                                            key={index}
                                                            flagShowLinkedChatbot
                                                            flagDelete={false}
                                                            title={`Slack Account ${index + 1}`}
                                                            currentlySelected={true}
                                                            showRemoveChatbotIntegration={true}
                                                            disabled={channel.chatbot_slack_assignment_data
                                                                && channel.chatbot_slack_assignment_data
                                                                    .length > 0 ? true : false}
                                                            removeChatbotIntegration={(chatbot_slack_assignment_id: string) => {
                                                                // handleRemoveChatbotIntegration(chatbot_slack_assignment_id)
                                                                setDelinkedChannelDetails(({ ...delinkedChannelDetails, flagDelink: true, channelDetails: channel }))
                                                            }}

                                                            details={{
                                                                page_id: String(channel?.chatbot_slack_assignment_id),
                                                                integrated_channel_id: "",
                                                                page_name: `${channel?.slack_channel_name} - ${channel.username}`,
                                                                page_category: channel?.slack_channel_purpose,
                                                                linked_chatbots: [channel]
                                                            }}
                                                            flagIntegration={true}
                                                        />
                                                })}
                                            </Grid>
                                        </Grid>
                                        <Grid item >
                                            <GlobalButton buttonText={"Add Slack Channel"} className='blackShadowedButton' onClick={() => { setFlagShowAvailableChannels(true) }} startIcon={<IconsColor fillColor="#A5A5A5" iconName="plus" width="19px" height="19px" />} />
                                        </Grid>
                                    </Grid>
                                </>
                                :
                                <>
                                    <Grid item className={globalStyles.wpIntegrationWrapper} mt={"10px"} mb={"5px"} sx={{ width: "calc(100% - 3px) !important" }}>
                                        <Grid container className={globalStyles.flexColumn}>
                                            <Grid item>
                                                <span className={globalStyles.textWpInt}>This Chatbot is not linked with any Slack Channel Yet. </span>
                                            </Grid>
                                            <Grid item className={globalStyles.padTop}>
                                                <GlobalButton buttonText={"Add Slack Channel"} className='primaryButtonStyle' onClick={() => { setFlagShowAvailableChannels(true) }} sx={{ padding: "6px 20px !important" }} startIcon={<IconsColor fillColor="#FFFFFF" iconName="plus" width="15px" height="15px" />} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </>
                        }
                    </> :
                    <>
                        <NoAuthorisedSlackAccount handleIntegrateSlack={handleIntegrateSlack} />
                    </>}

                {flagShowAvailableChannels &&
                    <ShowAvailableSlackChannels
                        flagShowAvailableNumbers={flagShowAvailableChannels}
                        setFlagShowAvailableNumbers={setFlagShowAvailableChannels}
                        smallScreen={smallScreen}
                        showConfirmationPopover={confirmationPopover.flagOpen}
                        confirmationPopoverAnchorEl={confirmationPopover.anchorEl}
                        handleCloseConfirmationPopover={handleCloseConfirmationPopover}
                        AddChatbotIntegration={AddChatbotSlackIntegration}
                        availablePhoneNumberData={availablePagesData}
                        handleRemoveChatbotIntegration={() => { }}
                        displayPhoneNumber={confirmationPopover.channel_name}
                        launchWhatsAppSignup={handleIntegrateSlack}
                        saveChatbotIntegrationData={saveSlackIntegrationDetails}
                    />

                }
                {delinkedChannelDetails.flagDelink &&
                    <GlobalDeletePopup
                        deleteDescription={`Are you sure you want to delink ${delinkedChannelDetails?.channelDetails?.slack_channel_name || ""} channel from ${chatBotDetails?.chatbot_name}`}
                        open={delinkedChannelDetails.flagDelink}
                        onClose={() => { setDelinkedChannelDetails(({ ...delinkedChannelDetails, flagDelink: false, channelDetails: null })) }}
                        onDelete={() => {
                            handleRemoveChatbotIntegration(String(delinkedChannelDetails?.channelDetails?.chatbot_slack_assignment_id || ""))
                        }}
                    />
                }
            </>
        </>
    );
};

export default ChatbotSlackIntegration;
